import * as React from "react";
import { Check } from "lucide-react";

export interface Step {
  id: string;
  title: string;
  description?: string;
}

export interface StepperProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  className?: string;
}

const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  ({ steps, currentStep, onStepClick, className = "" }, ref) => {
    return (
      <div ref={ref} className={`w-full ${className}`}>
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isCompleted = index < currentStep;
            const isCurrent = index === currentStep;
            const isClickable = onStepClick && (isCompleted || index === currentStep + 1);

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={() => isClickable && onStepClick?.(index)}
                    disabled={!isClickable}
                    className={`
                      flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors
                      ${isCompleted
                        ? "border-primary bg-primary text-primary-foreground"
                        : isCurrent
                          ? "border-primary bg-background text-primary"
                          : "border-muted bg-background text-muted-foreground"
                      }
                      ${isClickable ? "cursor-pointer hover:bg-primary/10" : "cursor-default"}
                    `}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      index + 1
                    )}
                  </button>
                  <div className="mt-2 text-center">
                    <p
                      className={`text-sm font-medium ${
                        isCurrent ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {step.title}
                    </p>
                    {step.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {step.description}
                      </p>
                    )}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-4 ${
                      index < currentStep ? "bg-primary" : "bg-muted"
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }
);
Stepper.displayName = "Stepper";

export interface StepContentProps {
  children: React.ReactNode;
  className?: string;
}

const StepContent = React.forwardRef<HTMLDivElement, StepContentProps>(
  ({ children, className = "" }, ref) => {
    return (
      <div ref={ref} className={`mt-8 ${className}`}>
        {children}
      </div>
    );
  }
);
StepContent.displayName = "StepContent";

export interface StepActionsProps {
  children: React.ReactNode;
  className?: string;
}

const StepActions = React.forwardRef<HTMLDivElement, StepActionsProps>(
  ({ children, className = "" }, ref) => {
    return (
      <div
        ref={ref}
        className={`mt-8 flex items-center justify-between ${className}`}
      >
        {children}
      </div>
    );
  }
);
StepActions.displayName = "StepActions";

export { Stepper, StepContent, StepActions };
